import React, { useEffect, useState } from "react";
import { api, fmtGNF, openAuthedFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Receipt, Upload, CheckCircle2, Clock, XCircle, FileText, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";

const METHOD_LABELS = {
  bank_transfer: "Virement bancaire",
  bank_deposit: "Dépôt au guichet",
  orange_money: "Orange Money (code marchand)",
  mobile_money_manual: "Mobile Money (manuel)",
  other: "Autre",
};

const STATUS_UI = {
  pending: { label: "En attente", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  validated: { label: "Validé", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  rejected: { label: "Rejeté", color: "bg-red-100 text-red-800", icon: XCircle },
};

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function ParentReceiptsPage() {
  const [payments, setPayments] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [omCfg, setOmCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    payment_id: "", method: "bank_transfer", bank_name: "",
    reference_number: "", paid_amount: 0, paid_date: new Date().toISOString().slice(0, 10),
    notes: "", file: null,
  });

  const load = async () => {
    try {
      const [p, r, om] = await Promise.all([
        api.get("/payments"),
        api.get("/erp/parent-receipts"),
        api.get("/orange-money/config"),
      ]);
      setPayments(p.data.filter(x => x.status !== "paid"));
      setReceipts(r.data);
      setOmCfg(om.data);
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, []);

  const openFor = (p) => {
    setForm({
      payment_id: p.id, method: "bank_transfer", bank_name: "", reference_number: "",
      paid_amount: p.amount, paid_date: new Date().toISOString().slice(0, 10), notes: "", file: null,
    });
    setOpen(true);
  };

  const isOM = form.method === "orange_money";

  const submit = async () => {
    if (isOM && !form.reference_number.trim()) return toast.error("Saisissez l'ID de la transaction Orange Money (reçu par SMS)");
    if (!isOM && !form.file) return toast.error("Sélectionnez un fichier reçu (PDF/JPG/PNG)");
    if (form.file && form.file.size > 8_000_000) return toast.error("Fichier trop volumineux (max 8 Mo)");
    setSaving(true);
    try {
      const b64 = form.file ? await fileToBase64(form.file) : null;
      await api.post("/erp/parent-receipts", {
        payment_id: form.payment_id,
        method: form.method,
        bank_name: isOM ? "Orange Money" : form.bank_name,
        reference_number: form.reference_number,
        paid_amount: parseFloat(form.paid_amount),
        paid_date: form.paid_date,
        notes: form.notes,
        file_data_b64: b64,
      });
      toast.success("Paiement déclaré ! En attente de validation par le service financier.");
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="parent-receipts-page">
      <div>
        <div className="chip mb-2"><Receipt className="h-3 w-3" /><span>REÇUS BANCAIRES</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Mes reçus de paiement</h1>
        <p className="text-slate-500 mt-1">
          Après un versement bancaire ou dépôt au guichet, uploadez le reçu ici. Après validation par la comptabilité, le reçu officiel de l&apos;école sera généré automatiquement.
        </p>
      </div>

      {/* Orange Money — carte info + QR */}
      {omCfg?.enabled && (
        <div className="rounded-2xl border-2 border-[#FF7900]/30 bg-gradient-to-br from-orange-50 to-white shadow-sm overflow-hidden" data-testid="orange-money-card">
          <div className="px-5 py-3 bg-[#FF7900] text-white font-heading font-bold flex items-center gap-2">
            <Smartphone className="h-4 w-4" />Payer avec Orange Money
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5 items-center">
            <div className="space-y-3">
              <div>
                <div className="text-xs uppercase text-slate-500 font-semibold">Marchand</div>
                <div className="font-heading font-bold text-[#1E3A8A]">{omCfg.merchant_name}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-slate-500 font-semibold">Code marchand</div>
                <div className="font-mono font-extrabold text-3xl text-[#FF7900] tracking-wider" data-testid="om-merchant-code">{omCfg.merchant_code}</div>
              </div>
              <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                {(omCfg.instructions || []).map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </div>
            <div className="flex flex-col items-center gap-2">
              <img src={`${api.defaults.baseURL}/orange-money/qrcode`} alt="QR Orange Money"
                   className="w-56 rounded-xl border border-orange-200 shadow" data-testid="om-qrcode" />
              <div className="text-xs text-slate-500 text-center">Scannez le QR code avec votre téléphone,<br />puis déclarez votre paiement ci-dessous.</div>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A]">Paiements en attente</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">N° Reçu</th>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-right">Montant</th>
                <th className="px-4 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`pending-payment-${p.id}`}>
                  <td className="px-4 py-2 font-mono text-xs text-[#1E3A8A]">{p.receipt_number}</td>
                  <td className="px-4 py-2">{p.type}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold">{fmtGNF(p.amount)}</td>
                  <td className="px-4 py-2 text-right">
                    {p.parent_receipt_uploaded ? (
                      <span className="text-xs text-slate-500">Reçu envoyé</span>
                    ) : (
                      <Button size="sm" onClick={() => openFor(p)} className="bg-[#EA580C] hover:bg-orange-700 text-white gap-1 text-xs" data-testid={`upload-btn-${p.id}`}>
                        <Upload className="h-3 w-3" />Uploader reçu
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {payments.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">Aucun paiement en attente</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* My uploaded receipts */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A]">Mes reçus envoyés</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Date envoi</th>
                <th className="px-4 py-2 text-left">Mode</th>
                <th className="px-4 py-2 text-left">Banque / Réf.</th>
                <th className="px-4 py-2 text-right">Montant</th>
                <th className="px-4 py-2 text-center">Statut</th>
                <th className="px-4 py-2 text-right">Preuve</th>
                <th className="px-4 py-2 text-right">Reçu officiel</th>
              </tr>
            </thead>
            <tbody>
              {receipts.map(r => {
                const st = STATUS_UI[r.status] || STATUS_UI.pending;
                const Ico = st.icon;
                return (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`receipt-${r.id}`}>
                    <td className="px-4 py-2 text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="px-4 py-2">{METHOD_LABELS[r.method]}</td>
                    <td className="px-4 py-2 text-xs">{r.bank_name} <br /><span className="text-slate-400 font-mono">{r.reference_number}</span></td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold">{fmtGNF(r.paid_amount)}</td>
                    <td className="px-4 py-2 text-center"><span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold ${st.color}`}><Ico className="h-3 w-3" />{st.label}</span></td>
                    <td className="px-4 py-2 text-right">
                      {r.file_path ? (
                        <button
                          type="button"
                          onClick={() => openAuthedFile(`/erp/parent-receipts/${r.id}/file`).catch(() => toast.error("Impossible d'ouvrir la preuve"))}
                          className="text-xs text-[#1E3A8A] hover:underline inline-flex items-center gap-1"
                          data-testid={`proof-btn-${r.id}`}
                        >
                          <FileText className="h-3 w-3" />Voir
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {r.status === "validated" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openAuthedFile(`/payments/${r.payment_id}/receipt`).catch(() => toast.error("Impossible d'ouvrir le reçu officiel"))}
                          className="text-xs gap-1 border-green-600 text-green-700 hover:bg-green-50"
                          data-testid={`official-receipt-btn-${r.id}`}
                        >
                          <FileText className="h-3 w-3" />Télécharger PDF
                        </Button>
                      ) : (
                        <span className="text-xs text-slate-400">Après validation</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {receipts.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Aucun reçu envoyé</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isOM ? "Déclarer un paiement Orange Money" : "Uploader un reçu bancaire"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Mode</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger data-testid="pr-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(METHOD_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date du paiement</Label>
                <Input type="date" value={form.paid_date} onChange={e => setForm({ ...form, paid_date: e.target.value })} data-testid="pr-date" />
              </div>
            </div>
            {isOM && omCfg && (
              <div className="rounded-xl border border-[#FF7900]/40 bg-orange-50 p-3 text-sm" data-testid="om-dialog-panel">
                <div className="font-semibold text-[#FF7900]">Code marchand : <span className="font-mono text-lg">{omCfg.merchant_code}</span> — {omCfg.merchant_name}</div>
                <div className="text-slate-600 mt-1">Composez <strong>{omCfg.ussd_code}</strong> → Paiement Marchand → code <strong>{omCfg.merchant_code}</strong> → montant → code secret. Saisissez ensuite l&apos;ID de transaction reçu par SMS.</div>
              </div>
            )}
            {!isOM && (
              <div>
                <Label>Banque / Opérateur</Label>
                <Input value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} placeholder="Ex : BSIC, Ecobank, Orange Money" data-testid="pr-bank" />
              </div>
            )}
            <div>
              <Label>{isOM ? "ID de transaction Orange Money (SMS) *" : "Numéro de référence"}</Label>
              <Input value={form.reference_number} onChange={e => setForm({ ...form, reference_number: e.target.value })} placeholder={isOM ? "Ex : PP260213.1234.A56789" : "Réf. bordereau, transaction..."} data-testid="pr-ref" />
            </div>
            <div>
              <Label>Montant payé (GNF)</Label>
              <Input type="number" value={form.paid_amount} onChange={e => setForm({ ...form, paid_amount: e.target.value })} data-testid="pr-amount" />
            </div>
            <div>
              <Label>{isOM ? "Capture du SMS de confirmation (optionnel)" : "Fichier reçu (PDF/JPG/PNG, max 8 Mo) *"}</Label>
              <Input type="file" accept="application/pdf,image/*" onChange={e => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="pr-file" />
              {form.file && <div className="text-xs text-slate-500 mt-1">Fichier : {form.file.name} ({(form.file.size / 1024).toFixed(0)} Ko)</div>}
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Précisions éventuelles..." data-testid="pr-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#166534] hover:bg-[#14532d] text-white" data-testid="pr-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
